import { apiClient as api } from "./client";
import type {
  Contact,
  ContactRequest,
  Note,
  CreateNoteRequest,
  UpdateNoteRequest,
  DealType,
  DealTypeRequest,
  Deal,
  DealRequest,
  CrmTask,
  CrmTaskRequest,
  ActivityNote,
  NoteAttachment,
  Project,
  ProjectRequest,
  TimelineEntry,
  SearchResponse,
} from "@/types/crm";

// Contacts API
export const contactsApi = {
  getAll: async (): Promise<Contact[]> => {
    return api.get<Contact[]>("/api/contacts");
  },

  getById: async (id: string): Promise<Contact> => {
    return api.get<Contact>(`/api/contacts/${id}`);
  },

  getEmployees: async (companyId: string): Promise<Contact[]> => {
    return api.get<Contact[]>(`/api/contacts/${companyId}/employees`);
  },

  create: async (data: ContactRequest): Promise<Contact> => {
    return api.post<Contact>("/api/contacts", data);
  },

  update: async (id: string, data: ContactRequest): Promise<Contact> => {
    return api.put<Contact>(`/api/contacts/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return api.delete(`/api/contacts/${id}`);
  },
};

// Notes API (unified)
export const notesApi = {
  getAll: async (): Promise<Note[]> => api.get<Note[]>('/api/notes'),
  getByEntity: async (entityType: string, entityId: string, includeEmployees = false): Promise<Note[]> => {
    const params = new URLSearchParams({ entityType, entityId });
    if (includeEmployees) params.set("includeEmployees", "true");
    return api.get<Note[]>(`/api/notes?${params}`);
  },
  create: async (data: CreateNoteRequest): Promise<Note> =>
    api.post<Note>("/api/notes", data),
  update: async (noteId: string, data: UpdateNoteRequest): Promise<Note> =>
    api.put<Note>(`/api/notes/${noteId}`, data),
  delete: async (noteId: string): Promise<void> =>
    api.delete(`/api/notes/${noteId}`),
};

// Deal Types API
export const dealTypesApi = {
  getAll: async (): Promise<DealType[]> => {
    return api.get<DealType[]>("/api/deal-types");
  },

  getById: async (id: string): Promise<DealType> => {
    return api.get<DealType>(`/api/deal-types/${id}`);
  },

  create: async (data: DealTypeRequest): Promise<DealType> => {
    return api.post<DealType>("/api/deal-types", data);
  },

  update: async (id: string, data: DealTypeRequest): Promise<DealType> => {
    return api.put<DealType>(`/api/deal-types/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return api.delete(`/api/deal-types/${id}`);
  },
};

// Deals API
export const dealsApi = {
  getAll: async (params?: { contactId?: string; projectId?: string }): Promise<Deal[]> => {
    const searchParams = new URLSearchParams();
    if (params?.contactId) searchParams.set("contactId", params.contactId);
    if (params?.projectId) searchParams.set("projectId", params.projectId);
    const query = searchParams.toString();
    return api.get<Deal[]>(`/api/deals${query ? `?${query}` : ""}`);
  },

  getByContact: async (contactId: string): Promise<Deal[]> => {
    return api.get<Deal[]>(`/api/deals?contactId=${contactId}`);
  },

  getById: async (id: string): Promise<Deal> => {
    return api.get<Deal>(`/api/deals/${id}`);
  },

  create: async (data: DealRequest): Promise<Deal> => {
    return api.post<Deal>("/api/deals", data);
  },

  update: async (id: string, data: DealRequest): Promise<Deal> => {
    return api.put<Deal>(`/api/deals/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return api.delete(`/api/deals/${id}`);
  },
};

// Projects API
export const projectsApi = {
  getAll: async (params?: { contactId?: string }): Promise<Project[]> => {
    const searchParams = new URLSearchParams();
    if (params?.contactId) searchParams.set("contactId", params.contactId);
    const query = searchParams.toString();
    return api.get<Project[]>(`/api/projects${query ? `?${query}` : ""}`);
  },

  getById: async (id: string): Promise<Project> => {
    return api.get<Project>(`/api/projects/${id}`);
  },

  create: async (data: ProjectRequest): Promise<Project> => {
    return api.post<Project>("/api/projects", data);
  },

  update: async (id: string, data: ProjectRequest): Promise<Project> => {
    return api.put<Project>(`/api/projects/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return api.delete(`/api/projects/${id}`);
  },
};

// Project Activity API
export const projectActivityApi = {
  getActivity: async (projectId: string): Promise<ActivityNote[]> => {
    return api.get<ActivityNote[]>(`/api/projects/${projectId}/activity`);
  },
};

// Tasks API
export const tasksApi = {
  getAll: async (params?: { contactId?: string; dealId?: string; status?: string; projectId?: string }): Promise<CrmTask[]> => {
    const searchParams = new URLSearchParams();
    if (params?.contactId) searchParams.set("contactId", params.contactId);
    if (params?.dealId) searchParams.set("dealId", params.dealId);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.projectId) searchParams.set("projectId", params.projectId);
    const query = searchParams.toString();
    return api.get<CrmTask[]>(`/api/tasks${query ? `?${query}` : ""}`);
  },

  getById: async (id: string): Promise<CrmTask> => {
    return api.get<CrmTask>(`/api/tasks/${id}`);
  },

  create: async (data: CrmTaskRequest): Promise<CrmTask> => {
    return api.post<CrmTask>("/api/tasks", data);
  },

  update: async (id: string, data: CrmTaskRequest): Promise<CrmTask> => {
    return api.put<CrmTask>(`/api/tasks/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return api.delete(`/api/tasks/${id}`);
  },
};

// Activity API
export const activityApi = {
  getNotes: async (contactId: string): Promise<ActivityNote[]> => {
    return api.get<ActivityNote[]>(`/api/contacts/${contactId}/activity`);
  },
  getDealActivity: async (dealId: string): Promise<ActivityNote[]> => {
    return api.get<ActivityNote[]>(`/api/deals/${dealId}/activity`);
  },
};

// Note Attachments API
export const noteAttachmentsApi = {
  getByNote: async (noteId: string): Promise<NoteAttachment[]> =>
    api.get<NoteAttachment[]>(`/api/notes/${noteId}/attachments`),

  upload: async (
    noteId: string,
    files: File[]
  ): Promise<NoteAttachment[]> => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    const token = localStorage.getItem("token");
    const baseUrl = import.meta.env.VITE_API_URL ?? "";
    const response = await fetch(
      `${baseUrl}/api/notes/${noteId}/attachments`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || "Upload failed");
    }
    if (response.status === 204) return [];
    return response.json();
  },

  delete: async (
    noteId: string,
    attachmentId: string
  ): Promise<void> => {
    return api.delete(
      `/api/notes/${noteId}/attachments/${attachmentId}`
    );
  },
};

// Seed API
export type SeedSize = "small" | "medium" | "large";

export interface SeedResult {
  companies: number;
  people: number;
  dealTypes: number;
  deals: number;
  projects: number;
  tasks: number;
  notes: number;
}

export const seedApi = {
  seed: async (size: SeedSize = "medium"): Promise<SeedResult> => {
    return api.post<SeedResult>("/api/seed", { size });
  },
};

// Timeline API
export const timelineApi = {
  getGlobal: (take = 20, before?: string) => {
    const params = new URLSearchParams({ take: String(take) });
    if (before) params.set("before", before);
    return api.get<TimelineEntry[]>(`/api/timeline?${params}`);
  },
  getEntity: (entityType: string, entityId: string, take = 20, before?: string) => {
    const params = new URLSearchParams({ take: String(take) });
    if (before) params.set("before", before);
    return api.get<TimelineEntry[]>(`/api/timeline/${entityType}/${entityId}?${params}`);
  },
};

// Search API
export const searchApi = {
  search: (q: string, limit = 5): Promise<SearchResponse> => {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return api.get<SearchResponse>(`/api/search?${params}`);
  },
};
